import React, { useMemo } from 'react';

import { SidePage, Section } from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';
import { usePages } from '@gogame/screens';

import {
  UserFormDuplicate,
  UserFormEdit,
} from 'scenes/Users/AdminUsers/components/UserForm';
import {
  UserPermissions,
  PermissionList,
  PermissionListCategory,
} from 'scenes/Users/components/UserPermissions';
import {
  CATEGORY_ACTIONS,
  CATEGORY_AFFILIATES,
  CATEGORY_APPLICATIONS,
  CATEGORY_CUSTOMER_COMPANIES,
  CATEGORY_NETWORKS,
  CATEGORY_PAGES,
} from 'scenes/Users/components/UserPermissions/constants';
import { mapPageToOptions } from 'scenes/Users/utils';

import { useLoadUserDetails } from '../../data';
import Actions from '../Actions';
import Metadata from './Metadata';
import UserInformation from './UserInformation';
import { usePagesWithExternalLinks } from '../../../hooks';
import externalLinks from 'App/externalLinks';

const UserDetails = ({ id, onClose, onExited, open }) => {
  const [childComponent, openChildComponent] = useChildComponent();
  const { data, isLoading, isFetching } = useLoadUserDetails(id);

  const pages = usePages(mapPageToOptions, { allowDuplicatePermission: false });
  const pagesWithExternalLinks = usePagesWithExternalLinks({
    pages,
    externalLinks: externalLinks,
  });

  const permissionsPages = data && data.permissions && data.permissions.pages;

  const filteredPages = useMemo(() => {
    if (permissionsPages) {
      return permissionsPages
        .map(({ id }) => pagesWithExternalLinks.find((p) => p.id === id))
        .filter((p) => p);
    } else {
      return [];
    }
  }, [permissionsPages, pagesWithExternalLinks]);

  const handleDuplicate = () => {
    openChildComponent(UserFormDuplicate, {
      id: data.id,
    });
  };

  const handleEdit = () => {
    openChildComponent(UserFormEdit, {
      id: data.id,
    });
  };

  const { actions, customerCompanies, affiliates, networks, applications } =
    data?.permissions || {};

  const {
    actions: actionsTotal,
    customerCompanies: customerCompaniesTotal,
    affiliates: affiliatesTotal,
    networks: networksTotal,
    applications: applicationsTotal,
  } = data?.permissionTotals || {};

  return (
    <SidePage
      backdrop={false}
      onClose={onClose}
      onExited={onExited}
      loading={isLoading}
      fetching={isFetching}
      open={open}
      size={'lg'}
    >
      <SidePage.Title
        title={`User ${data && data.status.id === 1 ? '(Blocked)' : ''}`}
        entityId={id}
        subTitle={data && `${data.name}`}
      />

      <SidePage.NewBody wrapper={true} dense={true}>
        <SidePage.NewContent>
          <Section title={'User Information'}>
            <UserInformation data={data} />
          </Section>
          <Section
            title={'Permissions'}
            fullHeight={true}
            isCollapsable
            initialOpen={true}
          >
            <UserPermissions>
              <PermissionListCategory
                id={CATEGORY_ACTIONS}
                label={'Actions'}
                options={actions}
                total={actionsTotal}
              >
                <PermissionList />
              </PermissionListCategory>
              <PermissionListCategory
                id={CATEGORY_PAGES}
                label={'Pages'}
                options={filteredPages}
                total={filteredPages.length}
              >
                <PermissionList />
              </PermissionListCategory>
              <PermissionListCategory
                id={CATEGORY_CUSTOMER_COMPANIES}
                label={'Customer Companies'}
                options={customerCompanies}
                total={customerCompaniesTotal}
              >
                <PermissionList />
              </PermissionListCategory>
              <PermissionListCategory
                id={CATEGORY_AFFILIATES}
                label={'Affiliates'}
                options={affiliates}
                total={affiliatesTotal}
              >
                <PermissionList />
              </PermissionListCategory>
              <PermissionListCategory
                id={CATEGORY_NETWORKS}
                label={'Networks'}
                options={networks}
                total={networksTotal}
              >
                <PermissionList />
              </PermissionListCategory>
              <PermissionListCategory
                id={CATEGORY_APPLICATIONS}
                label={'Applications'}
                options={applications}
                total={applicationsTotal}
              >
                <PermissionList />
              </PermissionListCategory>
            </UserPermissions>
          </Section>
        </SidePage.NewContent>
        <SidePage.Sidebar
          actions={
            <Actions
              data={data}
              actions={data?.actions}
              onEdit={handleEdit}
              onDuplicate={handleDuplicate}
            />
          }
        >
          <Metadata data={data} />
        </SidePage.Sidebar>
      </SidePage.NewBody>
      <SidePage.Actions>
        <SidePage.CloseButton />
      </SidePage.Actions>
      {childComponent}
    </SidePage>
  );
};

export default UserDetails;
